import React from 'react';
import propTypes from 'prop-types';
import { FormGroup, Label, Input, Row, Col, Badge } from 'reactstrap';
import ChecklistBox, { singleOf, idsOf } from '../../../components/Export/ChecklistBox';

const sectionStyle = { fontSize: '.66rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase' };
const labelStyle = { fontWeight: 600, fontSize: '.78rem' };

export const ACTIVE_OPTIONS = [{ id: '1', name: 'Active' }, { id: '0', name: 'Inactive' }];
export const TYPE_OPTIONS = [{ id: 'percentage', name: 'Percentage' }, { id: 'nominal', name: 'Nominal' }];
export const DEDUCT_OPTIONS = [{ id: 'product_price', name: 'Product price' }, { id: 'courier_price', name: 'Courier price' }];
export const VOUCHER_TYPE_OPTIONS = [{ id: 'public', name: 'Public' }, { id: 'public_open', name: 'Public open' }, { id: 'private', name: 'Private' }, { id: 'in_store', name: 'In store' }];
export const PLATFORM_OPTIONS = [{ id: 'APP', name: 'App' }, { id: 'WEB', name: 'Web' }];
export const FLAG_OPTIONS = [
  { id: 'is_cashback', name: 'Cashback' }, { id: 'disposable_voucher', name: 'Disposable' },
  { id: 'new_user_only', name: 'New user only' }, { id: 'auto_apply', name: 'Auto apply' }
];
export const SKUS_OPTIONS = [{ id: 'with', name: 'With SKUs' }, { id: 'without', name: 'Without SKUs' }];
export const SECOND_FILE_OPTIONS = [{ id: 'sku_rows', name: 'Voucher × SKU rows' }];

export const VOUCHER_EXPORT_DEFAULTS = {
  groups: [],
  keyword: '',
  active: [],
  type: [],
  deduct_type: [],
  voucher_type: [],
  platform: [],
  flags: [],
  skus: [],
  second_file: [],
  date_field: 'ended_at',
  date_from: '',
  date_to: ''
};

const Field = ({ id, label, children }) => (
  <FormGroup>
    <Label for={id} className="mb-1" style={labelStyle}>{label}</Label>
    {children}
  </FormGroup>
);

// VoucherExportFilters is the Vouchers block inside ExportModal. loadGroups
// reads GET /admins/vouchers/groups so the Group box offers the names in use.
export default function VoucherExportFilters({ filters, setFilter, disabled, loadGroups }) {
  const box = (name, props) => (
    <ChecklistBox id={`_exportVoucher_${name}`} value={filters[name]} onChange={v => setFilter(name, v)} disabled={disabled} {...props} />
  );
  return (
    <div>
      <div className="text-muted mb-2" style={sectionStyle}>Voucher</div>
      <Row>
        <Col md={6}><Field id="_exportVoucher_groups" label="Group">{box('groups', { loadOptions: loadGroups, searchable: true, placeholder: 'All groups' })}</Field></Col>
        <Col md={6}><Field id="_exportVoucher_keyword" label="Keyword">
          <Input id="_exportVoucher_keyword" bsSize="sm" value={filters.keyword} disabled={disabled} placeholder="Code" onChange={e => setFilter('keyword', e.target.value)} />
        </Field></Col>
      </Row>
      <Row>
        <Col md={4}><Field id="_exportVoucher_active" label="Active">{box('active', { options: ACTIVE_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportVoucher_type" label="Type">{box('type', { options: TYPE_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportVoucher_deduct_type" label="Deduct from">{box('deduct_type', { options: DEDUCT_OPTIONS })}</Field></Col>
      </Row>
      <Row>
        <Col md={4}><Field id="_exportVoucher_voucher_type" label="Voucher type">{box('voucher_type', { options: VOUCHER_TYPE_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportVoucher_platform" label="Platform">{box('platform', { options: PLATFORM_OPTIONS })}</Field></Col>
        <Col md={4}><Field id="_exportVoucher_flags" label="Flags">{box('flags', { options: FLAG_OPTIONS, placeholder: 'Any' })}</Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>Dates</div>
      <Row>
        <Col md={4}><Field id="_exportVoucher_date_field" label="Date field">
          <Input id="_exportVoucher_date_field" bsSize="sm" type="select" value={filters.date_field} disabled={disabled} onChange={e => setFilter('date_field', e.target.value)}>
            <option value="started_at">Started at</option>
            <option value="ended_at">Ended at</option>
            <option value="created_at">Created at</option>
            <option value="updated_at">Updated at</option>
          </Input>
        </Field></Col>
        <Col md={4}><Field id="_exportVoucher_date_from" label="From">
          <Input id="_exportVoucher_date_from" bsSize="sm" type="date" value={filters.date_from} disabled={disabled} onChange={e => setFilter('date_from', e.target.value)} />
        </Field></Col>
        <Col md={4}><Field id="_exportVoucher_date_to" label="To">
          <Input id="_exportVoucher_date_to" bsSize="sm" type="date" value={filters.date_to} disabled={disabled} onChange={e => setFilter('date_to', e.target.value)} />
        </Field></Col>
      </Row>

      <div className="text-muted mb-2 mt-1" style={sectionStyle}>SKUs <Badge color="light" className="border" style={{ fontSize: '.6rem' }}>email only</Badge></div>
      <Row>
        <Col md={6}><Field id="_exportVoucher_skus" label="Attached SKUs">{box('skus', { options: SKUS_OPTIONS })}</Field></Col>
        <Col md={6}><Field id="_exportVoucher_second_file" label="Second file">{box('second_file', { options: SECOND_FILE_OPTIONS, placeholder: 'None' })}</Field></Col>
      </Row>
    </div>
  );
}

VoucherExportFilters.propTypes = {
  filters: propTypes.object.isRequired,
  setFilter: propTypes.func.isRequired,
  disabled: propTypes.bool,
  loadGroups: propTypes.func.isRequired
};

// toVoucherParams maps the modal's values onto Go /admins/vouchers query
// parameters. The panel's own params come first; an empty box inherits the
// panel's value, a pick overrides it.
export const toVoucherParams = (panelParams, f) => {
  const { page, per_page, sort_by, ...base } = panelParams;
  const params = { ...base };
  const override = (key, value) => {
    if (value !== '' && value !== null && value !== undefined) params[key] = value;
  };
  override('keyword', (f.keyword || '').trim());
  override('active', singleOf(f.active, ACTIVE_OPTIONS));
  override('type', idsOf(f.type));
  override('deduct_type', idsOf(f.deduct_type));
  override('voucher_type', idsOf(f.voucher_type));
  override('platform_specifications', idsOf(f.platform));
  override('group_name', idsOf(f.groups));
  override('flags', idsOf(f.flags));
  if (f.date_from || f.date_to) {
    override('date_field', f.date_field);
    override('date_from', f.date_from);
    override('date_to', f.date_to);
  }
  return params;
};

// Email-only parameters: SKU presence and the second file.
export const toVoucherEmailParams = f => {
  const params = {};
  const skus = singleOf(f.skus, SKUS_OPTIONS);
  if (skus) params.skus = skus;
  if ((f.second_file || []).some(o => o.id === 'sku_rows')) params.sku_rows = '1';
  return params;
};

export const hasEmailOnlyFilters = f => Boolean(singleOf(f.skus, SKUS_OPTIONS));
